import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import { useLocation, useNavigate } from 'react-router-dom';

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Array<{ sender: string; content: string }>>([]);
  const [chat_history, setChatHistory] = useState<Array<Array<string>>>([]); // [chat_history, setChatHistory
  const [input, setInput] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);
  const [isBotThinking, setIsBotThinking] = useState(false);
  const endOfMessagesRef = useRef<null | HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [height, setHeight] = useState(window.innerHeight);

  const initialMessageSent = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.state && location.state.userInput && !initialMessageSent.current) {
      sendMessage(location.state.userInput);
      initialMessageSent.current = true;
      navigate('', { state: { userInput: '' } });
    }
  }, []);

  const sendMessage = async (content: string) => {
    setMessages((prevMessages) => [...prevMessages, { sender: 'user', content }]);
    setInput('');
    setIsBotThinking(true); // Start bot thinking

    try {
      const query = content;
      const response = await fetch('https://iitgpt.webnd-iitbbs.org/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, chat_history: chat_history }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data?.answer) {
        setMessages((prevMessages) => [...prevMessages, { sender: 'bot', content: data.answer }]);
        setChatHistory((prevChatHistory) => [...prevChatHistory, [query, data.answer]]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Handle the error state properly here, potentially notify the user
    } finally {
      setIsBotThinking(false); // Stop bot thinking
    }
  };

  const samplePrompts = [
    "Tell me the fee structure of Dual Degree",
    "What is Students' Gymkhana?",
    "Give me some tips for my Signals and Systems exam"
  ];

  const scrollToBottom = () => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  return (
    <div style={{ height }} className="w-full flex flex-col items-center justify-between relative antialiased pt-8 md:pt-10">
      <Header showPrompts={showPrompts} togglePrompts={() => setShowPrompts(!showPrompts)}>
        {showPrompts && (
          <div className="mt-4 p-4 bg-blue-200 rounded-lg shadow-lg absolute z-20 right-0 w-64">
            <h2 className="text-lg font-bold mb-2">Sample Prompts:</h2>
            <ul>
              {samplePrompts.map((prompt, index) => (
                <li key={index} onClick={() => { setInput(prompt); setShowPrompts(!showPrompts); if (inputRef.current) inputRef.current.focus(); }} style={{ cursor: 'pointer' }} className='pb-1'>{prompt}</li>
              ))}
              <li className="mt-3">
                <a href="https://forms.gle/PU2yG3mzWJgBQZdZ8" target="_blank" rel="noopener noreferrer" className="text-blue-900 font-medium ">
                  Want to contribute? Help us improve by providing your input!
                </a>
              </li>
            </ul>
          </div>
        )}
      </Header>

      <div className="w-full max-w-4xl mx-auto py-4 px-2 z-10 flex flex-col h-full justify-between">
        <div className="flex-grow overflow-auto p-4 mb-4 space-y-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`whitespace-pre-wrap rounded-lg p-2 ${message.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'} max-w-[80%]`}>
                {message.content}
              </div>
            </div>
          ))}
          {isBotThinking && (
            <div className="flex justify-start">
              <div className="rounded-lg p-2 bg-gray-200 text-gray-800">
                <span className="typing-indicator"></span>
              </div>
            </div>
          )}
          <div ref={endOfMessagesRef} />
        </div>
        <div className="p-4 border-t-2 border-gray-200">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim() !== '') {
                sendMessage(input);
              }
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter your query..."
              className="flex-1 rounded-lg border-2 border-gray-300 p-2 focus:outline-none focus:border-blue-500 bg-transparent text-neutral-200"
              ref={inputRef}
            />
            <button
              type="submit"
              disabled={input.trim() === ''}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-4 py-2 focus:outline-none ${input.trim() === '' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;